import React, { useState, useEffect } from 'react';
import { Bell, Heart, MessageCircle, UserPlus, Trophy, Check, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface Notification {
  id: string;
  type: 'like' | 'comment' | 'follow' | 'follow_request' | 'follow_accepted' | 'achievement';
  message: string;
  user: {
    id?: string;
    name: string;
    avatar?: string;
  };
  createdAt: string;
  read: boolean;
  relatedFollowRequest?: string;
  chatId?: string;
  isAlreadyFollowing?: boolean; // Check if already following before showing buttons
}

const NotificationBell: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [moderationUnreadCount, setModerationUnreadCount] = useState(0);
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);

  useEffect(() => {
    loadNotifications();
    loadModerationUnreadCount();
    // Set up polling for new notifications
    const interval = setInterval(() => {
      loadNotifications();
      loadModerationUnreadCount();
    }, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadModerationUnreadCount = async () => {
    try {
      const response = await api.get('/user/notifications/unread-count');
      if (response.data?.success) {
        setModerationUnreadCount(response.data.data.moderation || 0);
      }
    } catch (error) {
      console.error('Failed to load moderation unread count:', error);
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      if (response.data?.success) {
        const notifs = response.data.data.notifications || [];
        
        // Filter out follow_request notifications where we're already following
        // This is a safety check in case backend didn't filter them
        const filteredNotifs = await Promise.all(
          notifs.map(async (notif: Notification) => {
            if (notif.type === 'follow_request' && notif.user.id) {
              try {
                const followStatusRes = await api.get(`/users/${notif.user.id}/follow-status`);
                if (followStatusRes.data.success) {
                  const followStatus = followStatusRes.data.data;
                  if (followStatus.isFollowing) {
                    // Already following, don't show this notification
                    return null;
                  }
                  // Add flag to notification
                  notif.isAlreadyFollowing = followStatus.isFollowing;
                }
              } catch (error) {
                // If check fails, still show the notification
                console.error('Failed to check follow status:', error);
              }
            }
            return notif;
          })
        );
        
        const validNotifs = filteredNotifs.filter((n): n is Notification => n !== null);
        setNotifications(validNotifs);
        setUnreadCount(validNotifs.filter((n: Notification) => !n.read).length);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleAcceptFollowRequest = async (notification: Notification, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!notification.user.id) {
      toast.error('Invalid notification data');
      return;
    }

    try {
      setProcessingRequest(notification.id);
      const response = await api.post(`/users/${notification.user.id}/follow/accept`);
      
      if (response.data.success) {
        // Remove notification or update it
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
        setUnreadCount(prev => Math.max(0, prev - 1));
        toast.success('Follow request accepted!');
        
        // Reload notifications to ensure UI is in sync
        loadNotifications();
        
        // Navigate to chat if created
        if (response.data.data.chat) {
          setTimeout(() => {
            navigate(`/chats/${response.data.data.chat.id}`);
            setShowDropdown(false);
            toast.success('Chat opened!');
          }, 500);
        }
      }
    } catch (error: any) {
      console.error('Failed to accept follow request:', error);
      const errorMessage = error.response?.data?.error?.message || 'Failed to accept follow request';
      
      // If request not found, it might have been already processed
      if (errorMessage.includes('not found') || error.response?.status === 404) {
        // Remove notification since request is already processed
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
        setUnreadCount(prev => Math.max(0, prev - 1));
        toast('Follow request was already processed', { icon: 'ℹ️' });
        // Reload notifications to ensure UI is in sync
        loadNotifications();
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleRejectFollowRequest = async (notification: Notification, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!notification.user.id) {
      toast.error('Invalid notification data');
      return;
    }

    try {
      setProcessingRequest(notification.id);
      const response = await api.post(`/users/${notification.user.id}/follow/reject`);
      
      if (response.data.success) {
        // Remove notification
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
        setUnreadCount(prev => Math.max(0, prev - 1));
        toast.success('Follow request rejected');
        
        // Reload notifications to ensure UI is in sync
        loadNotifications();
      }
    } catch (error: any) {
      console.error('Failed to reject follow request:', error);
      const errorMessage = error.response?.data?.error?.message || 'Failed to reject follow request';
      
      // If request not found, it might have been already processed
      if (errorMessage.includes('not found') || error.response?.status === 404) {
        // Remove notification since request is already processed
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
        setUnreadCount(prev => Math.max(0, prev - 1));
        toast('Follow request was already processed', { icon: 'ℹ️' });
        // Reload notifications to ensure UI is in sync
        loadNotifications();
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    
    // Navigate based on notification type
    if (notification.type === 'follow_accepted' && notification.chatId) {
      navigate(`/chats/${notification.chatId}`);
      setShowDropdown(false);
    } else if (notification.user.id) {
      navigate(`/profile/${notification.user.id}`);
      setShowDropdown(false);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'like': return <Heart className="w-4 h-4 text-red-500" />;
      case 'comment': return <MessageCircle className="w-4 h-4 text-blue-500" />;
      case 'follow': return <UserPlus className="w-4 h-4 text-green-500" />;
      case 'follow_request': return <UserPlus className="w-4 h-4 text-blue-500" />;
      case 'follow_accepted': return <Check className="w-4 h-4 text-green-500" />;
      case 'achievement': return <Trophy className="w-4 h-4 text-yellow-500" />;
      default: return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors"
      >
        <Bell className="w-6 h-6" />
        {(unreadCount + moderationUnreadCount) > 0 && (
          <span className={`absolute -top-1 -right-1 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center ${
            moderationUnreadCount > 0 ? 'bg-red-500' : 'bg-blue-500'
          }`}>
            {(unreadCount + moderationUnreadCount) > 9 ? '9+' : (unreadCount + moderationUnreadCount)}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 top-12 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No notifications yet
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 border-b border-gray-100 ${
                    !notification.read ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p 
                        className="text-sm text-gray-900 cursor-pointer"
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <span className="font-medium">{notification.user.name}</span>{' '}
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatTime(notification.createdAt)}
                      </p>
                      
                      {/* Accept/Reject buttons for follow requests - only show if not already following */}
                      {notification.type === 'follow_request' && !notification.read && !notification.isAlreadyFollowing && (
                        <div className="flex items-center space-x-2 mt-2">
                          <button
                            onClick={(e) => handleAcceptFollowRequest(notification, e)}
                            disabled={processingRequest === notification.id}
                            className="px-3 py-1.5 bg-blue-500 text-white text-xs font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                          >
                            <Check className="w-3 h-3" />
                            <span>Accept</span>
                          </button>
                          <button
                            onClick={(e) => handleRejectFollowRequest(notification, e)}
                            disabled={processingRequest === notification.id}
                            className="px-3 py-1.5 bg-gray-200 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                          >
                            <X className="w-3 h-3" />
                            <span>Reject</span>
                          </button>
                        </div>
                      )}
                      {/* Show "Already Following" message if follow request but already following */}
                      {notification.type === 'follow_request' && notification.isAlreadyFollowing && (
                        <div className="mt-2 text-xs text-gray-500 italic">
                          Already following
                        </div>
                      )}
                    </div>
                    {!notification.read && notification.type !== 'follow_request' && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          
          {notifications.length > 0 && (
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={async () => {
                  try {
                    await api.put('/notifications/read-all');
                  } catch { /* best effort */ }
                  setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                  setUnreadCount(0);
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Mark all as read
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;